import { useState, useEffect } from 'react';
import clsx from 'clsx';
import { Modal } from './Modal';
import { downloadExport } from '../services/exportService';
import { api } from '../services/api';
import type { ExportScope, TripSummary } from '@poi-planner/shared';

interface ExportModalProps {
  open: boolean;
  onClose: () => void;
}

export function ExportModal({ open, onClose }: ExportModalProps) {
  const [scope, setScope] = useState<ExportScope>('library');
  const [trips, setTrips] = useState<TripSummary[]>([]);
  const [selectedTripId, setSelectedTripId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch trips when modal opens
  useEffect(() => {
    if (!open) return;

    const fetchTrips = async () => {
      try {
        const tripList = await api.listTrips();
        setTrips(
          tripList.map((t) => ({
            id: t.id,
            name: t.name,
            createdAt: t.createdAt,
            updatedAt: t.updatedAt,
            startDate: t.startDate || '',
            endDate: t.endDate || '',
            dayCount: t.dayCount || 0,
          }))
        );
      } catch (err) {
        console.error('Failed to load trips:', err);
      }
    };

    fetchTrips();
  }, [open]);

  const handleExport = async () => {
    setLoading(true);
    setError(null);

    try {
      await downloadExport({
        scope,
        tripId: scope === 'trip' ? selectedTripId ?? undefined : undefined,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setLoading(false);
    }
  };

  const canExport = scope !== 'trip' || selectedTripId;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Export Data"
      icon="download"
      width="450px"
      footer={
        <>
          <button onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={!canExport || loading}
            className="btn-primary disabled:opacity-50"
          >
            {loading ? 'Exporting...' : 'Download JSON'}
          </button>
        </>
      }
    >
      <div className="space-y-5">
        <p className="text-sm text-text-secondary">
          Export your POI Planner data as a JSON file. You can use this for backup or
          to import into another tool.
        </p>

        {/* Scope selection */}
        <div className="space-y-3">
          <label className="text-xs font-medium text-text-tertiary uppercase tracking-wide">
            What to export
          </label>

          <div className="space-y-2">
            {/* Library only */}
            <button
              onClick={() => setScope('library')}
              className={clsx(
                'w-full flex items-start gap-3 p-3 rounded-lg border-2 transition-all text-left',
                scope === 'library'
                  ? 'border-text-primary bg-slate-50'
                  : 'border-border hover:border-text-secondary'
              )}
            >
              <span
                className={clsx(
                  'material-symbols-outlined text-xl mt-0.5',
                  scope === 'library' ? 'text-text-primary' : 'text-text-muted'
                )}
              >
                folder_special
              </span>
              <div>
                <div className="text-sm font-medium text-text-primary">Library Only</div>
                <div className="text-xs text-text-secondary mt-0.5">
                  All POIs and tags (no trip schedules)
                </div>
              </div>
            </button>

            {/* Full takeout */}
            <button
              onClick={() => setScope('all')}
              className={clsx(
                'w-full flex items-start gap-3 p-3 rounded-lg border-2 transition-all text-left',
                scope === 'all'
                  ? 'border-text-primary bg-slate-50'
                  : 'border-border hover:border-text-secondary'
              )}
            >
              <span
                className={clsx(
                  'material-symbols-outlined text-xl mt-0.5',
                  scope === 'all' ? 'text-text-primary' : 'text-text-muted'
                )}
              >
                inventory_2
              </span>
              <div>
                <div className="text-sm font-medium text-text-primary">Full Export</div>
                <div className="text-xs text-text-secondary mt-0.5">
                  Library + all trips and schedules
                </div>
              </div>
            </button>

            {/* Single trip */}
            <button
              onClick={() => setScope('trip')}
              className={clsx(
                'w-full flex items-start gap-3 p-3 rounded-lg border-2 transition-all text-left',
                scope === 'trip'
                  ? 'border-text-primary bg-slate-50'
                  : 'border-border hover:border-text-secondary'
              )}
            >
              <span
                className={clsx(
                  'material-symbols-outlined text-xl mt-0.5',
                  scope === 'trip' ? 'text-text-primary' : 'text-text-muted'
                )}
              >
                luggage
              </span>
              <div>
                <div className="text-sm font-medium text-text-primary">Single Trip</div>
                <div className="text-xs text-text-secondary mt-0.5">
                  One trip with its referenced POIs
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Trip selector (when scope is 'trip') */}
        {scope === 'trip' && (
          <div className="space-y-2">
            <label className="text-xs font-medium text-text-tertiary uppercase tracking-wide">
              Select trip
            </label>
            <select
              value={selectedTripId || ''}
              onChange={(e) => setSelectedTripId(e.target.value || null)}
              className="w-full px-3 py-2 text-sm bg-input-bg border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent"
            >
              <option value="">-- Select a trip --</option>
              {trips.map((trip) => (
                <option key={trip.id} value={trip.id}>
                  {trip.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-600">
            <span className="material-symbols-outlined text-base">error</span>
            {error}
          </div>
        )}

        {/* Info box */}
        <div className="bg-surface-secondary rounded-lg p-3 text-xs text-text-secondary">
          <p>
            The exported JSON file follows the POI Planner schema and can be re-imported
            or used with external tools.
          </p>
        </div>
      </div>
    </Modal>
  );
}
